import React, { useEffect, useState } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    RupeeIcon,
    CombineInputGroup,
    ModalHeader,
} from "../Styles/InputStyles";
import { Select, MenuItem, FormControl } from "@mui/material";
import { MdEdit, MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";

const validationSchema = Yup.object({
    incrementType: Yup.number(),

    incrementValue: Yup.number()
        .typeError("Increment value must be a number")
        .when('incrementType', {
            is: (incrementType) => Boolean(incrementType),
            then: (schema) => schema
                .required("Increment value is required when increment type is selected")
                .when('incrementType', {
                    is: 1,
                    then: (schema) => schema
                        .min(1, "Flat amount cannot be negative or 0")
                        .max(500000, "Flat amount cannot exceed ₹5,00,000"),
                    otherwise: (schema) => schema
                        .min(1, "Percentage cannot be negative or 0")
                        .max(100, "Percentage cannot exceed 100%")
                }),
            otherwise: (schema) => schema
        }),

    monthlyRent: Yup.number()
        .typeError("Rent must be a number")
        .required("Rent is required")
        .min(1000, "Rent must be at least ₹1,000")
        .max(5000000, "Rent cannot exceed ₹50,00,000"),

    security: Yup.number()
        .typeError("Deposit must be a number")
        .required("Deposit is required")
        .min(0, "Deposit cannot be negative")
        .max(5000000, "Deposit cannot exceed ₹50,00,000"),

    agreementPeriod: Yup.number()
        .required("Agreement Period is required")
        .min(1, "Agreement Period must be at least 1 month")
        .max(60, "Agreement Period cannot exceed 60 months"),

    agreementCharges: Yup.number()
        .typeError("Agreement Charges must be a number")
        .nullable()
        .transform((value, originalValue) =>
            originalValue === '' ? null : value
        )
        .test(
            'agreement-charges-range',
            'Agreement Charges must be between ₹1 and ₹5,00,000',
            function (value) {
                if (value === null || value === undefined || value === '') {
                    return true;
                }
                return value >= 1 && value <= 500000;
            }
        ),

    noticePeriod: Yup.number()
        .required("Notice Period is required"),

    lockInPeriod: Yup.number()
        .required("Lock-In Period is required"),
});

const AgreementRenew = ({ renewAgreementMutate, renewAgreementLoading, isRenewAgreement, handleClose, selectedData }) => {

    const calculateNewRent = (incrementType, incrementValue, baseRent) => {
        const base = parseFloat(baseRent) || 0;
        const increment = parseFloat(incrementValue) || 0;

        if (incrementType === 1) {
            return base + increment;
        } else if (incrementType === 2) {
            return base + (base * increment / 100);
        }
        return base;
    };

    const calculateRentIncreaseAmount = (incrementType, incrementValue, baseRent) => {
        const base = parseFloat(baseRent) || 0;
        const increment = parseFloat(incrementValue) || 0;

        if (incrementType === 1) {
            return increment;
        } else if (incrementType === 2) {
            return (base * increment / 100);
        }
        return 0;
    };

    const getInitialValues = () => {
        const baseRent = selectedData.rent || "";
        const baseSecurity = selectedData.security || 0;
        const incrementType = selectedData?.agreementSetting?.rentIncrementType || "";
        const incrementValue = selectedData?.agreementSetting?.rentIncrementValue || "";
        const updateSecurity = selectedData?.agreementSetting?.updateSecurity || false;

        let initialRent = baseRent;
        let initialSecurity = baseSecurity;

        if (incrementType && incrementValue) {
            initialRent = calculateNewRent(incrementType, incrementValue, baseRent);

            if (updateSecurity) {
                const rentIncreaseAmount = calculateRentIncreaseAmount(incrementType, incrementValue, baseRent);
                initialSecurity = baseSecurity + rentIncreaseAmount;
            }
        }

        return {
            incrementType: incrementType,
            incrementValue: incrementValue,
            increaseSecurity: updateSecurity,
            monthlyRent: initialRent,
            security: initialSecurity,
            agreementPeriod: 11,
            agreementCharges: selectedData?.agreementSetting?.agreementCharges || "",
            noticePeriod: 1,
            lockInPeriod: 1,
        };
    };

    const initialValues = getInitialValues();

    const [height, setHeight] = useState(window.innerHeight);
    const [originalSecurity, setOriginalSecurity] = useState(selectedData.security || 0);
    const [originalRent, setOriginalRent] = useState(selectedData.rent || '');
    const [editMonthlyRent, setEditMonthlyRent] = useState(false);
    const [editSecurity, setEditSecurity] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleSubmit = (values) => {
        const expiryDate = new Date(selectedData.agreementExpiry);
        expiryDate.setDate(expiryDate.getDate() + 1);
        const nextDay = expiryDate.toISOString().split('T')[0];

        renewAgreementMutate({
            rent: Number(values.monthlyRent || 0),
            security: Number(values.security || 0),
            agreementPeriod: values.agreementPeriod,
            agreementCharges: values.agreementCharges,
            noticePeriod: values.noticePeriod,
            lockInPeriod: values.lockInPeriod,
            occupancyId: selectedData.id,
            agreementStartDate: nextDay,
        });
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Renew Agreement</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, errors, touched, handleChange, handleBlur }) => {

                        const isMonthlyRentEditable = Boolean(values.incrementType) && Boolean(values.incrementValue);

                        const handleFieldChange = (field, value) => {
                            setFieldValue(field, value);
                        };

                        const handleIncrementTypeChange = (value) => {
                            setFieldValue('incrementType', value);
                            setFieldValue('incrementValue', '');
                            setFieldValue('monthlyRent', originalRent);
                            setFieldValue('security', originalSecurity);
                            setFieldValue('increaseSecurity', false);
                        };

                        const handleIncrementValueChange = (value) => {
                            setFieldValue('incrementValue', value);

                            if (value && values.incrementType) {
                                const newRent = calculateNewRent(
                                    values.incrementType,
                                    value,
                                    originalRent
                                );
                                setFieldValue('monthlyRent', newRent);

                                if (values.increaseSecurity) {
                                    const rentIncreaseAmount = calculateRentIncreaseAmount(
                                        values.incrementType,
                                        value,
                                        originalRent
                                    );
                                    const newSecurity = originalSecurity + rentIncreaseAmount;
                                    setFieldValue('security', newSecurity);
                                }
                            } else if (!value && values.incrementType) {
                                setFieldValue('monthlyRent', originalRent);
                                if (values.increaseSecurity) {
                                    setFieldValue('security', originalSecurity);
                                }
                            }
                        };

                        const handleSecurityCheckboxChange = (checked) => {
                            if (checked) {
                                if (values.incrementType && values.incrementValue) {
                                    const rentIncreaseAmount = calculateRentIncreaseAmount(
                                        values.incrementType,
                                        values.incrementValue,
                                        originalRent
                                    );
                                    const newSecurity = originalSecurity + rentIncreaseAmount;
                                    setFieldValue('security', newSecurity);
                                }
                            } else {
                                setFieldValue('security', originalSecurity);
                            }
                            setFieldValue('increaseSecurity', checked);
                        };

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                    <Wrapper>
                                        {/* Increment Type */}
                                        <InputGroup>
                                            <Label htmlFor="incrementType">Increment Type</Label>

                                            <FormControl fullWidth>
                                                <Select
                                                    id="incrementType"
                                                    name="incrementType"
                                                    value={values.incrementType || ""}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value === "") {
                                                            setFieldValue("incrementType", "");
                                                            handleIncrementTypeChange(value);
                                                        } else {
                                                            handleIncrementTypeChange(value);
                                                        }
                                                    }}
                                                    displayEmpty
                                                    style={{ height: "46px" }}
                                                    className="px-2"
                                                    renderValue={(selected) => {
                                                        if (selected === "") {
                                                            return <span className="text-gray-400">Select increment type</span>;
                                                        }
                                                        return selected === 1
                                                            ? "Flat Amount (₹)"
                                                            : "Percentage (%)";
                                                    }}
                                                >

                                                    <MenuItem value="">
                                                        <em className="text-gray-400">Select increment type</em>
                                                    </MenuItem>

                                                    <MenuItem value={1}>Flat Amount (₹)</MenuItem>
                                                    <MenuItem value={2}>Percentage (%)</MenuItem>
                                                </Select>
                                            </FormControl>

                                            {errors.incrementType && touched.incrementType && (
                                                <ErrorMessage>{errors.incrementType}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        {/* Increment Value */}
                                        {values.incrementType && (
                                            <InputGroup>
                                                <Label htmlFor="incrementValue">
                                                    {values.incrementType === 1 ? 'Flat Amount (₹)' : 'Percentage (%)'} *
                                                </Label>
                                                <FieldWrappers>
                                                    {values.incrementType === 1 && <RupeeIcon />}
                                                    <Field
                                                        type="number"
                                                        name="incrementValue"
                                                        id="incrementValue"
                                                        placeholder={values.incrementType === 1 ? "Enter amount" : "Enter % increase"}
                                                        value={values.incrementValue}
                                                        onChange={(e) => handleIncrementValueChange(e.target.value)}
                                                        className={values.incrementType === 1 ? "pl-6" : ""}
                                                    />
                                                    {values.incrementType === 2 && (
                                                        <span className="absolute right-3 text-gray-500">%</span>
                                                    )}
                                                </FieldWrappers>
                                                {errors.incrementValue && touched.incrementValue && (
                                                    <ErrorMessage>{errors.incrementValue}</ErrorMessage>
                                                )}
                                            </InputGroup>
                                        )}

                                        {/* Increase Security Checkbox */}
                                        {values.incrementType && values.incrementValue && (
                                            <InputGroup>
                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        name="increaseSecurity"
                                                        id="increaseSecurity"
                                                        checked={values.increaseSecurity}
                                                        onChange={(e) => handleSecurityCheckboxChange(e.target.checked)}
                                                        className="mr-2 h-4 w-4 accent-primary-color"
                                                    />
                                                    <Label htmlFor="increaseSecurity" className="mb-0 cursor-pointer">
                                                        Apply same increment amount to security deposit
                                                    </Label>
                                                </div>
                                            </InputGroup>
                                        )}

                                        {/* Monthly Rent (Read-only) */}
                                        <CombineInputGroup>
                                            <InputGroup>
                                                <Label htmlFor="monthlyRent">
                                                    Monthly Rent *
                                                </Label>
                                                <FieldWrappers >
                                                    <RupeeIcon />
                                                    <Field
                                                        type="number"
                                                        name="monthlyRent"
                                                        id="monthlyRent"
                                                        value={values.monthlyRent || ''}
                                                        readOnly={!editMonthlyRent}
                                                        className={`pl-6`}
                                                    />
                                                    {isMonthlyRentEditable && !editMonthlyRent && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditMonthlyRent(true)}
                                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#0078BD]"
                                                        >
                                                            <MdEdit size={18} />
                                                        </button>
                                                    )}
                                                </FieldWrappers>
                                                {errors.monthlyRent && touched.monthlyRent && (
                                                    <ErrorMessage>{errors.monthlyRent}</ErrorMessage>
                                                )}
                                            </InputGroup>

                                            {/* Security Deposit */}
                                            <InputGroup>
                                                <Label htmlFor="security">Security Deposit *</Label>
                                                <FieldWrappers >
                                                    <RupeeIcon />
                                                    <Field
                                                        type="number"
                                                        name="security"
                                                        id="security"
                                                        value={values.security}
                                                        readOnly={!editSecurity}
                                                        className="pl-6"
                                                    />
                                                    {isMonthlyRentEditable && !editSecurity && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditSecurity(true)}
                                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#0078BD]"
                                                        >
                                                            <MdEdit size={18} />
                                                        </button>
                                                    )}
                                                </FieldWrappers>
                                                {errors.security && touched.security && (
                                                    <ErrorMessage>{errors.security}</ErrorMessage>
                                                )}
                                            </InputGroup>
                                        </CombineInputGroup>
                                        {/* Agreement Period (Normal Input) */}
                                        <InputGroup>
                                            <Label htmlFor="agreementPeriod">Agreement Period (Months) *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="number"
                                                    name="agreementPeriod"
                                                    id="agreementPeriod"
                                                    value={values.agreementPeriod}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    className="pl-3"
                                                />
                                            </FieldWrappers>
                                            {errors.agreementPeriod && touched.agreementPeriod && (
                                                <ErrorMessage>{errors.agreementPeriod}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        {/* Agreement Charges */}
                                        <InputGroup>
                                            <Label htmlFor="agreementCharges">Agreement Charges (₹)</Label>
                                            <FieldWrappers>
                                                <RupeeIcon />
                                                <Field
                                                    type="number"
                                                    name="agreementCharges"
                                                    id="agreementCharges"
                                                    placeholder="Enter agreement charges"
                                                    value={values.agreementCharges}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    className="pl-6"
                                                />
                                            </FieldWrappers>
                                            {errors.agreementCharges && touched.agreementCharges && (
                                                <ErrorMessage>{errors.agreementCharges}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                        {/* Notice Period */}
                                        <CombineInputGroup>
                                            <InputGroup>
                                                <Label htmlFor="noticePeriod">Notice Period *</Label>
                                                <FormControl fullWidth>
                                                    <Select
                                                        className="px-2"
                                                        id="noticePeriod"
                                                        name="noticePeriod"
                                                        value={values.noticePeriod}
                                                        onChange={(e) => setFieldValue("noticePeriod", e.target.value)}
                                                        required
                                                        style={{ height: "46px" }}
                                                    >
                                                        {Array.from({ length: 7 }, (_, i) => i).map((month) => (
                                                            <MenuItem key={month} value={month}>
                                                                {month} {month === 1 ? "Month" : "Months"}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                {errors.noticePeriod && touched.noticePeriod && (
                                                    <ErrorMessage>{errors.noticePeriod}</ErrorMessage>
                                                )}
                                            </InputGroup>

                                            {/* Lock-In Period */}
                                            <InputGroup>
                                                <Label htmlFor="lockInPeriod">Lock-In Period *</Label>
                                                <FormControl fullWidth>
                                                    <Select
                                                        className="px-2"
                                                        id="lockInPeriod"
                                                        name="lockInPeriod"
                                                        value={values.lockInPeriod}
                                                        onChange={(e) => setFieldValue("lockInPeriod", e.target.value)}
                                                        required
                                                        style={{ height: "46px" }}
                                                    >
                                                        {Array.from({ length: 11 }, (_, i) => i + 1).map(
                                                            (month) => (
                                                                <MenuItem key={month} value={month}>
                                                                    {month} {month === 1 ? "Month" : "Months"}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Select>
                                                </FormControl>
                                                {errors.lockInPeriod && touched.lockInPeriod && (
                                                    <ErrorMessage>{errors.lockInPeriod}</ErrorMessage>
                                                )}
                                            </InputGroup>
                                        </CombineInputGroup>
                                    </Wrapper>
                                </ScrollableContent>
                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={handleClose}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit" disabled={renewAgreementLoading}>
                                        {renewAgreementLoading ? "Submitting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-[#0078BD] py-4 px-4`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;

export default AgreementRenew;